/* eslint-disable ember/no-runloop */
import { set } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PRISM_SPACING_1000 } from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import GeneralOverlayWithMessage from 'lumen/components/general/overlay-with-message';
import ParityTooltipsTooltipTrends from 'lumen/components/parity/tooltips/tooltip-trends';
import { HIGHCHARTS_MISSING_MARKER } from 'lumen/utils/constants/general-constants';
import {
  PA_SORT_TYPES,
  SORT_TYPES,
} from 'lumen/utils/constants/pi-constant-values';
import { parityAnalyticsDateFormat } from 'lumen/utils/parity-analytics/date-utils';

interface ParityChartsChartTrendsSignature {
  Args: {
    data: unknown;
    dateAggregation: unknown;
    height: unknown;
    isLoading: unknown;
    segmentation: unknown;
    sortType: unknown;
    winMeetOrLoss: unknown;
  };
}

export default class ParityChartsChartTrends extends Component<ParityChartsChartTrendsSignature> {
  // eslint-disable-next-line ember/no-unused-services
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    // @ts-expect-error: Incorrect type
    const chartData = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.data.forEach((series) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const [data, zones] = this.createTrends(series.dataPoints);

      chartData.push({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        name: series.item.formattedSegmentationValue,
        zoneAxis: 'x',
        type: 'line',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        zones,
      });
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return chartData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    let enableLegend = false;

    if (!this.hasNoData) {
      enableLegend = true;
    }

    return {
      chart: {
        type: 'line',
        marginTop: PRISM_SPACING_1000,
        zoomType: null,
        height: this.args.height,
      },
      title: {
        text: 'parity-trends-chart',
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        enabled: enableLegend,
        borderWidth: 0,
        symbolWidth: 7,
        symbolHeight: 7,
        itemStyle: {
          width: 200,
          fontSize: 12,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
      tooltip: {
        hideDelay: 0.2,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        outside: true,

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const { chart } = this.series;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const { series } = chart;
          // Necessary to get correct data for tooltips, needs to be in separate runloop
          begin();
          const formattedExtractDate = parityAnalyticsDateFormat(
            // @ts-expect-error: Incorrect type
            dayjs.unix(this.x / 1000).utc(),
            // @ts-expect-error: Incorrect type
            that.args?.dateAggregation,
          );
          const formattedExtractDateEndOfWeek = parityAnalyticsDateFormat(
            dayjs
              // @ts-expect-error: Incorrect type
              .unix(this.x / 1000)
              .add(6, 'day')
              .utc(),
            // @ts-expect-error: Incorrect type
            that.args?.dateAggregation,
          );
          let displayName = `${formattedExtractDate} - ${formattedExtractDateEndOfWeek}`;

          if (that.args?.dateAggregation === 'month') {
            displayName = formattedExtractDate;
          }

          set(
            that,
            'violatorData',
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            series.map((serie) => {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              const point = that.findPointByXValue(serie, this.point.x);
              // @ts-expect-error: Incorrect type
              const { color } = point;

              return {
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                data: point.violator,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                name: serie.name,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                color,
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                selected: this.point.violator.id === point.violator.id,
              };
            }),
          );
          set(that, 'displayName', displayName);
          end();
          // @ts-expect-error: Incorrect type
          return document.getElementById('dummy-tooltip-for-trends').innerHTML;
        },
        useHTML: true,
      },
      xAxis: {
        allowDecimals: false,
        type: 'datetime',

        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            const dateAggregation = that.args?.dateAggregation;
            const formattedExtractDate = parityAnalyticsDateFormat(
              // @ts-expect-error: Incorrect type
              dayjs.unix(this.value / 1000).utc(),
              // @ts-expect-error: Incorrect type
              dateAggregation,
            );

            if (dateAggregation === 'month') {
              return formattedExtractDate;
            }

            const formattedExtractDateEndOfWeek = parityAnalyticsDateFormat(
              dayjs
                // @ts-expect-error: Incorrect type
                .unix(this.value / 1000)
                .add(6, 'day')
                .utc(),
              // @ts-expect-error: Incorrect type
              dateAggregation,
            );
            return `${formattedExtractDate} - ${formattedExtractDateEndOfWeek}`;
          },
        },
      },
      yAxis: {
        allowDecimals: that.args.sortType === SORT_TYPES.RELATIVE,
        endOnTick: true,
        ordinal: false,
        showFirstLabel: false,
        min: 0,
        ceiling: that.args.sortType === SORT_TYPES.RELATIVE ? 1 : null,
        title: {
          text: '',
        },
        labels: {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            if (that.args.sortType === SORT_TYPES.RELATIVE) {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              return that.intl.formatNumber(this.value, { style: 'percent' });
            }

            // @ts-expect-error: Incorrect type
            return `${this.value}`;
          },
        },
      },
      plotOptions: {
        series: {
          lineWidth: 2,
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNoData() {
    return (
      !this.args.isLoading &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.chartData.every((series) => series.data.hasNothingToDisplay)
    );
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  createTrends(violators) {
    // Making sure we set x to 0 for the zones not to get undefined values
    const previousPoint = { x: 0, y: null, hasNoData: false };
    // @ts-expect-error: Incorrect type
    const zones = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const sortType = PA_SORT_TYPES[this.args.sortType];

    // Flag data as missing when point are null so we don't ahave to re-iterate over it later on
    let hasNothingToDisplay = true;

    // Remember that violatorData consists of an extractDate and a violator object as made in route::invertViolatorsAndDates
    // violatorData = {
    //   violator1: [{extractDate: "2018-03-01", violator: segmentationValue}, ...](n=weeks),
    //   ...
    // }
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const data = violators.map((violatorData, index, allViolators) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const isLastViolator = allViolators.length - 1 === index;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const x = this.xValue(violatorData.extractDate);
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      let y = violatorData.violator[sortType]?.[this.args.winMeetOrLoss];
      let marker;

      // Check if current dataPoint has number of shops
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (violatorData.violator.hasNoData) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        violatorData.crossed = true;

        // Missing points always extend the previous y value
        y = previousPoint.y;
      } else {
        hasNothingToDisplay = false;
      }

      if (previousPoint.hasNoData) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        violatorData.dashed = true;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (violatorData.dashed) {
        zones.push({
          value: previousPoint.x,
          dashStyle: 'dot',
        });

        // If this is the last point, there will be no next point to set the zones on this point
        // So this sets a dotted zone for itself as the last point (else we have a solid segment in some cases where there is no data)
        if (isLastViolator) {
          zones.push({
            value: x,
            dashStyle: 'dot',
          });
        }
      } else {
        zones.push({
          value: previousPoint.x,
          dashStyle: 'solid',
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (violatorData.crossed) {
        marker = HIGHCHARTS_MISSING_MARKER;
      }

      // Remember point for padding in future potential missing data
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      previousPoint.y = y;
      previousPoint.x = x;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      previousPoint.hasNoData = violatorData.violator.hasNoData;

      return {
        x,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        y,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        violator: violatorData.violator,
        marker,
      };
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    data.hasNothingToDisplay = hasNothingToDisplay;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return [data, zones];
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  findPointByXValue(serie, xValue) {
    let point;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    serie.points.some((currentPoint) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (currentPoint.x === xValue) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        point = currentPoint;
        return true;
      }

      return false;
    });

    return point;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  xValue(timestamp) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return dayjs.utc(timestamp).unix() * 1000;
  }

  <template>
    <div class="relative">
      <GeneralChartsOtaChart
        {{! @glint-expect-error: Incorrect type }}
        @chartOptions={{this.chartOptions}}
        @model={{this.chartData}}
      />
      {{#if this.hasNoData}}
        <GeneralOverlayWithMessage @message={{t "general.no_data"}} />
      {{/if}}
    </div>
    <div class="parity-tooltip hidden" id="dummy-tooltip-for-trends">
      <ParityTooltipsTooltipTrends
        {{! @glint-expect-error: Incorrect type }}
        @displayName={{this.displayName}}
        {{! @glint-expect-error: Incorrect type }}
        @key={{@winMeetOrLoss}}
        {{! @glint-expect-error: Incorrect type }}
        @segmentation={{@segmentation}}
        {{! @glint-expect-error: Incorrect type }}
        @sortType={{@sortType}}
        {{! @glint-expect-error: Incorrect type }}
        @violatorData={{this.violatorData}}
      />
    </div>
  </template>
}
